import { createFileRoute, Link } from '@tanstack/react-router'
import { FeatureIcon, type FeatureIconName } from '#bv/components/FeatureIcons'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { LegalDocShell, LegalSectionCard } from '#bv/components/LegalDocShell'
import {
  PulseDigital_VOUCHES_CHANNEL,
  PulseDigital_VOUCHES_CHANNEL_URL,
  VOUCH_POST_DEADLINE_HOURS,
  vouchesChannelText,
} from '#bv/lib/replacementPolicy'

export const Route = createFileRoute('/refund')({
  component: RefundPage,
})

const SECTIONS: {
  title: string
  icon: FeatureIconName
  content: string
}[] = [
  {
    icon: 'shield',
    title: 'Our policy overview',
    content:
      'Sales are digital and instant. We prioritise replacements over cash refunds. Transactions are final unless we clearly say otherwise or we are at fault. No refunds for change of mind — know what you are buying. See our Terms for the full picture.',
  },
  {
    icon: 'check',
    title: 'Vouch to activate warranty',
    content:
      `After staff delivers your order, test it. If it works, post in ${vouchesChannelText()} within ${VOUCH_POST_DEADLINE_HOURS} hours with your order ID and product name. Your replacement window starts at that vouch (usually 24 hours from vouch for most listings; longer for some FA accounts — Terms §2). If it failed on first use, reply in your ticket before vouching — do not vouch a dead-on-arrival delivery.`,
  },
  {
    icon: 'zap',
    title: 'When to open a ticket',
    content:
      'Open a ticket for delivery and for replacement requests inside your active warranty window. Submit before the window expires (Terms §2). Include your order ID and screenshots or screen recordings showing the issue. Dead-on-arrival issues must be reported in-ticket before you vouch.',
  },
  {
    icon: 'check',
    title: 'When you qualify for a replacement',
    content:
      'Examples: delivery failure after a reasonable wait; credentials invalid as described; access lost without breach of rules on your side; boosts dropped inside the stated period. Screenshots and recordings are required. All claims are reviewed before approval.',
  },
  {
    icon: 'alert',
    title: 'When you do not qualify',
    content:
      'No replacement or refund if you broke platform rules, misused the product, ignored instructions, waited past your window, or never vouched within 24 hours of delivery (warranty not activated). Changing FA credentials against instructions can void support. External delays (processors, platforms) are not grounds for refund. Abuse or repeated chargebacks may lead to blacklist. Accounts tied to violations may be closed — those cases are not eligible for refund or replacement.',
  },
  {
    icon: 'money',
    title: 'Cash refunds',
    content:
      'Granted only when the issue is clearly our responsibility (e.g. verified non-delivery or duplicate charge on our side). Proof is mandatory. User mistakes, misuse, or third-party issues do not qualify. Contact us with your order ID; decisions are case-by-case.',
  },
  {
    icon: 'alert',
    title: 'Chargebacks',
    content:
      'Filing a chargeback without first contacting us is a violation of our terms and will result in immediate account termination and potential legal action. We are always willing to resolve issues — please contact us first. We have a 99% resolution rate.',
  },
  {
    icon: 'message',
    title: 'How to request a replacement',
    content:
      `Step 1: Join our Discord server (discord.gg/tGxhDfRkdE). Step 2: Open a support ticket for delivery or issues. Step 3: After delivery, vouch in ${vouchesChannelText()} within 24 hours if the product works (order ID + product required). Step 4: If something breaks inside your warranty window, open or update your ticket with screenshots and recordings. Alternatively, use the contact form on our website.`,
  },
]

function RefundPage() {
  return (
    <LegalDocShell
      eyebrow="Support"
      title="REFUND POLICY"
      lastUpdated="April 2026"
      intro={
        <>
          How replacements and refunds work at PulseDigital. This page matches our{' '}
          <Link to="/terms" className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 transition-colors hover:text-[#93c5fd]">
            Terms of Service
          </Link>
          . Product-specific windows (from your vouch) are listed in Terms §2. After delivery, post in{' '}
          <a
            href={PulseDigital_VOUCHES_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 transition-colors hover:text-[#93c5fd]"
          >
            {PulseDigital_VOUCHES_CHANNEL}
          </a>{' '}
          within {VOUCH_POST_DEADLINE_HOURS} hours to activate warranty; your replacement window runs from that vouch. Report dead-on-arrival issues in your ticket before vouching.
        </>
      }
    >
      <div className="space-y-10 sm:space-y-12">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(
            [
              {
                icon: 'shield' as FeatureIconName,
                title: 'Vouch within 24h',
                desc: (
                  <>
                    Post in{' '}
                    <a href={PulseDigital_VOUCHES_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#a5d8ff]">
                      {PulseDigital_VOUCHES_CHANNEL}
                    </a>{' '}
                    after delivery to start your warranty clock.
                  </>
                ),
              },
              {
                icon: 'zap' as FeatureIconName,
                title: 'Fast resolution',
                desc: 'Most tickets get a first response within a few hours.',
              },
              {
                icon: 'message' as FeatureIconName,
                title: 'Discord first',
                desc: `Tickets and ${PulseDigital_VOUCHES_CHANNEL} are both on our Discord server.`,
              },
            ]
          ).map((card) => (
            <div
              key={card.title}
              className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-transparent p-6 text-center shadow-lg shadow-black/30 transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(0, 191, 255,0.25)] hover:shadow-[0_20px_40px_-24px_rgba(0, 191, 255,0.35)]"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0, 191, 255,0.12), transparent 65%)',
                }}
              />
              <div className="relative flex justify-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[rgba(0,191,255,0.1)] text-[#8ecfff] ring-1 ring-[rgba(0,191,255,0.25)] transition-transform duration-300 group-hover:scale-105">
                  <FeatureIcon name={card.icon} size={30} className="text-current" />
                </span>
              </div>
              <div className="relative mt-4 font-display text-sm tracking-wide text-[#f0f6ff]">{card.title}</div>
              <div className="relative mt-2 text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
                {card.desc}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {SECTIONS.map((section) => (
            <LegalSectionCard
              key={section.title}
              title={
                <span className="inline-flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(123,47,190,0.12)] text-[#c4b5fd] ring-1 ring-[rgba(123,47,190,0.28)]">
                    <FeatureIcon name={section.icon} size={20} className="text-current" />
                  </span>
                  <span>{section.title}</span>
                </span>
              }
            >
              <p className="text-[#a8c4e8]">{section.content}</p>
            </LegalSectionCard>
          ))}
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-[rgba(0, 191, 255,0.22)] bg-gradient-to-br from-[rgba(0, 191, 255,0.1)] via-[rgba(88,101,242,0.06)] to-transparent p-6 shadow-xl shadow-black/40 sm:p-8">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-40 blur-3xl"
            style={{ background: '#5865F2' }}
          />
          <div className="relative">
            <p className="font-display text-lg tracking-wide text-[#f0f6ff] sm:text-xl">Need a replacement?</p>
            <p className="mt-2 max-w-lg text-sm leading-relaxed" style={{ color: '#8ecfff' }}>
              Open a ticket on Discord with your order ID — it&apos;s the fastest way to get help.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://discord.gg/tGxhDfRkdE"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-discord rounded-xl px-5 py-2.5 text-sm shadow-lg"
              >
                <DiscordIcon size={16} white className="shrink-0" />
                Open Discord ticket
              </a>
              <Link
                to="/contact"
                className="btn-chrome inline-flex items-center rounded-xl px-5 py-2.5 text-sm font-semibold"
              >
                Contact form
              </Link>
              <Link
                to="/terms"
                className="btn-link rounded-xl px-5 py-2.5 text-sm"
              >
                Read Terms §2
              </Link>
            </div>
          </div>
        </div>
      </div>
    </LegalDocShell>
  )
}
